import React, { Component } from "react";
import { connect } from "react-redux";

import Wizard from "sub_components/Forms/BloxWizard";
import { BloxButton } from "styled_components/Common/BloxButton";
import { Utils } from "services/utils";
import { BillingApi, BillingUtils } from "services/billing";
import { INVOICE_SUMMARY_HEADERS, MENU } from "utils/BillingConstants";
import {
  SITE_PAGES,
  SITE_MODULES,
  SuccessMessage,
  ErrorMessage,
} from "utils/CommonConstants";
import {
  updatePage,
  updateModule,
  addPageToBreadCrumbs,
} from "actions/siteTracking";
import { getUserPermissions, setUserPermissions } from "services/user";
import { updatePermissions } from "actions/permissions";
import { Permissions } from "services/permissions";
import PaymentMethodScreen from "./Components/PaymentMethod";
import PaymentSummary from "./Components/PaymentSummary";
import SelectInvoiceScreen from "./Components/SelectInvoiceScreen";

const CDN_URL = process.env.REACT_APP_CDN_URL;
const HeaderIcon = `${CDN_URL}billing/make-a-payment-icon.svg`;
const selectIconOn = `${CDN_URL}billing/Select-payment-method-icon-on.svg`;
const chequeIcon = `${CDN_URL}billing/select-invoice-icon-on.svg`;

const STAGES = {
  POPULATE: "POPULATE",
  REVIEW: "REVIEW",
  COMPLETE: "COMPLETE",
};
const HEADER_TITLE = {
  [STAGES.POPULATE]: "",
  [STAGES.REVIEW]: "REVIEW & Submit",
  [STAGES.COMPLETE]: "Confirmation",
};

class MakeAPayment extends Component {
  constructor(props) {
    super(props);

    this.billingApi = new BillingApi();
    this.commonParams = {};
    this.state = this.initializeWizard();
  }

  initializeWizard = () => {
    return {
      active: null,
      error: null,
      showModal: false,
      errorBody: null,
      ip: null,
      steps: {
        1: {
          component: SelectInvoiceScreen,
          name: "SelectInvoiceScreen",
          params: {
            ...this.commonParams,
            data: { invoices: [] },
          },
        },
        2: {
          component: PaymentMethodScreen,
          name: "PaymentMethodScreen",
          resetProgressOnEdit: true,
          params: { ...this.commonParams, data: {} },
        },
        REVIEW: {
          component: PaymentSummary,
          name: "PaymentReviewScreen",
          params: {
            ...this.commonParams,
            phase: "REVIEW",
            submitPayment: this.submitPayment,
            submitted: false,
          },
        },
        COMPLETE: {
          component: PaymentSummary,
          name: "PaymentSummaryScreen",
          params: {
            ...this.commonParams,
            phase: "CONFIRMATION",
          },
        },
      },
    };
  };

  resetWizard = () => {
    this.setState(() => {
      return { ...this.initializeWizard(), active: 1 };
    });
    setTimeout(() => {
      this.setState({ active: null });
    }, 1000);
  };

  formatPaymentParams = (data) => {
    const { companyInfo, auth_status: { user } = {} } = this.props;
    const { fuseBillId: customerId } = companyInfo;
    if (!data || !customerId) {
      return null;
    }
    const {
      1: {
        data: {
          total: amount,
          paymentAllocations: preFormattedPaymentAllocations,
        } = {},
      },
      2: {
        data: { nonce, cardholderName, cardType, lastFour: cardLastFour } = {},
      },
    } = data;
    const paymentAllocations = preFormattedPaymentAllocations.map((payment) => {
      return {
        invoiceID: payment.invoiceIdNumber,
        amount: Utils.formatAmount(payment.amount),
      };
    });

    return {
      userId: user?.id,
      customerId,
      amount,
      nonce,
      cardholderName,
      cardType,
      cardLastFour,
      paymentAllocations,
    };
  };

  submitPayment = async (data) => {
    const { ip } = this.state;
    const { setModalContent } = this.props;
    const paymentParams = this.formatPaymentParams(data);
    const button = (
      <BloxButton
        className="buttons41"
        id="okay"
        text="OKAY"
        module="billing"
        enabled={true}
        onClick={() => {}}
        cypressId="okay"
      />
    );
    if (!paymentParams) {
      setModalContent("TIMEOUT_CLOSE", ErrorMessage, button);
    }

    paymentParams.publicIpAddress = { ip: ip || "0.0.0.0" };
    paymentParams.privateIpAddress = "127.0.0.1";
    // eslint-disable-next-line
    this.setState((state) => (state.steps.REVIEW.params.submitted = true));

    try {
      const response = await BillingApi.submitPayment(paymentParams);
      if (Utils.isValidResponse(response)) {
        setModalContent("TIMEOUT_CLOSE", SuccessMessage, button);
        this.setState({
          active: STAGES.COMPLETE,
        });
      } else {
        // eslint-disable-next-line
        this.setState((state) => (state.steps.REVIEW.params.submitted = false));
        let errorBody;
        if (response.retryError) {
          errorBody = this.getSoftErrorBody();
        } else {
          errorBody = this.getHardErrorBody();
        }
        setModalContent("TIMEOUT_CLOSE", ErrorMessage, button);
        this.setState({ errorBody, error: "Network Error" });
      }
    } catch (e) {
      console.log(e);
      // eslint-disable-next-line
      this.setState((state) => (state.steps.REVIEW.params.submitted = false));
      setModalContent("TIMEOUT_CLOSE", ErrorMessage, button);
      this.setState({
        error: "Network Error",
      });
    }
  };

  setInvoices = (invoices) => {
    if (!invoices) {
      return;
    }

    const { steps } = this.state;
    const stepsCopy = { ...steps };
    stepsCopy["1"].params.data.invoices = invoices;

    this.setState({ invoices, steps: stepsCopy });
  };

  requestOverdueInvoices = async () => {
    const { companyInfo: { customer: { id, customerId = 0 } } = {} } =
      this.props;

    try {
      const response = await this.billingApi.getAll(
        id,
        ["Overdue", "Due"],
        customerId
      );
      if (Utils.isValidResponse(response)) {
        const massagedData = BillingUtils.massageData(
          response.data.invoices,
          INVOICE_SUMMARY_HEADERS
        );

        this.setInvoices(massagedData);
      }
    } catch (e) {
      console.log(e);
    }
  };

  componentDidUpdate() {
    const { data, permissions: pages } = this.props;
    const { invoices: existingInvoices } = this.state;

    if (data && !existingInvoices) {
      this.setInvoices(data);
    }

    if (pages?.permissions) {
      this.checkPage(pages?.permissions, "Make A Payment");
    }
  }
  checkPage = (pages, name) => {
    const PageToCheck = pages.filter((page) => page.displayName === name);

    if (!PageToCheck[0]?.permissions > 0) {
      throw new Error();
    }
  };
  getModulePages = async () => {
    const {
      updatePermissions,
      auth_status: {
        user: { id: userId },
      },
      company_info: {
        customer: { id: orgId },
      },
    } = this.props;

    //check cache
    const permissions = getUserPermissions("billing");
    if (!permissions) {
      const response = await Permissions.getModulePermissions(4, userId, orgId);
      if (response && !response.data.error) {
        setUserPermissions("billing", response.data.pages);
        updatePermissions(response.data.pages);
      }
    } else {
      updatePermissions(permissions);
    }
  };

  componentDidMount() {
    const {
      updatePage,
      updateModule,
      addPageToBreadCrumbs,
      site,
      permissions,
    } = this.props;
    updatePage(SITE_PAGES.BILLING[MENU.PAYMENT]);
    addPageToBreadCrumbs(
      SITE_PAGES.BILLING[MENU.PAYMENT],
      SITE_MODULES.BILLING
    );
    if (site?.module !== "BILLING" || !permissions?.permissions) {
      this.getModulePages();
    }
    Utils.addScroll();
    updateModule(SITE_MODULES.BILLING);
    this.requestOverdueInvoices();
  }

  render() {
    const { steps, active } = this.state;
    const { navigate } = this.props;
    const progressAttributes = [
      {
        key: 1,
        icon: chequeIcon,
        total: 1,
        completed: 0,
      },
      {
        key: 2,
        icon: selectIconOn,
        total: 5,
        completed: 0,
      },
    ];
    const pageTitle = HEADER_TITLE[active];
    const headerAttributes = {
      icon: HeaderIcon,
      moduleTitle: "MAKE A PAYMENT",
      pageTitle,
      progressAttributes,
    };
    return (
      <div className="online-payment-page">
        <Wizard
          headerAttributes={headerAttributes}
          steps={steps}
          active={active}
          resetWizard={this.resetWizard}
          navigate={navigate}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    companyInfo: state.company_info,
    auth_status: state.auth_status,
    site: state.site_tracking,
    permissions: state.permissions,
  };
}

export default connect(mapStateToProps, {
  updatePage,
  updateModule,
  addPageToBreadCrumbs,
  updatePermissions,
})(MakeAPayment);
